/* eslint-disable jsx-a11y/anchor-is-valid */
import { PartnershipTransactionRequest } from "@axl-dev/school-sdk";
import { Button, Form, Popconfirm } from "antd";
import { useWatch } from "antd/es/form/Form";
import { useState } from "react";

import { useLoc, useSdk } from "@app/shared/lib/hooks";
import { Loc, Price } from "@app/shared/ui";
import { FormBuilder } from "@app/shared/ui/FormBuilder";
import Request from "@app/shared/ui/Request";

import MaxAvailableAmount from "../MaxAvailableAmount/MaxAvailableAmount";

export type Props = {
  minAmount?: number;
  onSuccess: () => void;
  onCancel: () => void;
};

export const WithdrawalForm: React.FC<Props> = ({
  minAmount,
  onSuccess,
  onCancel,
}) => {
  const loc = useLoc();
  const sdk = useSdk();
  const [maxAmount, setMaxAmount] = useState(0);
  const [isCreating, setCreating] = useState(false);

  const [form] = Form.useForm();
  const amount = useWatch("amount", form);

  return (
    <Request
      action={async (values: {
        amount: number | null;
        comment: string | null;
      }) => {
        setCreating(true);
        const res = await sdk.partnership.requestStore.create(
          new PartnershipTransactionRequest({
            partnerComment: values.comment || "",
            amount: values.amount ?? 0,
          }),
        );
        setCreating(false);

        return res;
      }}
      onSuccess={onSuccess}
      successMsg={loc.word("Partnership.request.create.success", {
        default: "Withrawal request has been successfully created",
      })}
    >
      {({ exec }) => (
        <Form
          form={form}
          layout="vertical"
          onFinish={exec}
          initialValues={{ amount: 0, comment: "" }}
        >
          <FormBuilder.InputNumber
            name="amount"
            label={
              <Loc
                word="Partnership.withdrawal.amount"
                default="Enter amount to withdraw"
              />
            }
            extra={
              <div>
                {minAmount !== undefined && minAmount > 0 && (
                  <div>
                    <a
                      onClick={() => {
                        form.setFieldValue("amount", minAmount);
                      }}
                    >
                      <Loc
                        word="Partnership.transaction.minAvailableAmount"
                        default="Min amount to withdraw"
                      />
                      :&nbsp;
                      <Price
                        price={minAmount}
                        truncateIfFractionalZero
                        className="fw-500"
                      />
                    </a>
                  </div>
                )}
                <div>
                  <MaxAvailableAmount
                    onReady={setMaxAmount}
                    onClick={() => {
                      form.setFieldValue("amount", maxAmount);
                    }}
                  />
                </div>
              </div>
            }
            min={minAmount ?? 0}
            max={maxAmount}
            itemProps={{ style: { width: "100%" } }}
            rules={(x) => x.minNumber(minAmount ?? 1)}
          />

          <FormBuilder.TextArea
            name="comment"
            autoSize={{ minRows: 3, maxRows: 5 }}
            label={<Loc word="Global.comment" default="Comment" />}
          />

          <div className="flex gap-10">
            <Button onClick={onCancel} disabled={isCreating}>
              <Loc word="Global.cancel" default="Cancel" />
            </Button>
            <Popconfirm
              disabled={!amount || amount === 0}
              title={
                <Loc
                  word="Partnership.transaction.withdrawal.confirm"
                  default="Are you sure you want to withdraw {0}?"
                >
                  <Price price={amount} />
                </Loc>
              }
              onConfirm={form.submit}
            >
              <Button
                type="primary"
                disabled={!amount || amount === 0}
                loading={isCreating}
              >
                <Loc
                  word="Partnership.transaction.withdraw"
                  default="Withdraw"
                />
              </Button>
            </Popconfirm>
          </div>
        </Form>
      )}
    </Request>
  );
};
